import { api, type MotorResponse } from '../lib/api';
import { useResource } from '../lib/useResource';

/**
 * O MOTOR: o que esta rodando, o que parou e o que pede acao.
 *
 * Esta tela existe porque o monitor por estagio nao serve para nada sem alguem
 * que o leia, e porque rota sem nada na tela que a chame nao esta pronta.
 *
 * A distincao que ela precisa mostrar acima de tudo: fila com trabalho esperando
 * e SEM trabalhador registrado nao e fila lenta, e trabalho que nunca vai
 * acontecer. Nenhuma repeticao resolve, e mandar quem opera reprocessar seria
 * mandar consertar a coisa errada.
 */

const TIPO_LABEL: Record<string, string> = {
  sem_trabalhador: 'Etapa não construída',
  na_dead_letter: 'Esperando decisão humana',
  acumulando: 'Acumulando',
};

/** A cor segue a AÇÃO, e não a gravidade sentida. */
const TIPO_CLASSE: Record<string, string> = {
  sem_trabalhador: 'border-warn-border bg-warn-surface/60',
  na_dead_letter: 'border-danger-border bg-danger-surface',
  acumulando: 'border-line bg-surface',
};

export function Motor() {
  const recurso = useResource<MotorResponse>(() => api.motor(), []);

  if (recurso.kind === 'loading') {
    return (
      <div className="text-sm" aria-busy="true">
        <h1 className="text-2xl font-bold">Motor</h1>
        <p className="mt-2 text-ink-muted">Medindo as filas…</p>
      </div>
    );
  }

  if (recurso.kind === 'error') {
    return (
      <div className="max-w-3xl space-y-4 text-sm">
        <h1 className="text-2xl font-bold">Motor</h1>
        <div className="mt-4 rounded border border-danger-border bg-danger-surface p-4">
          <p className="font-medium">Não foi possível medir o motor.</p>
          <p className="mt-1">{recurso.message}</p>
          <p className="mt-2 text-xs text-ink-faint">
            Nenhum número aparece aqui: uma tela que degrada para zeros faria você concluir que o
            motor está sem trabalho. Código: {recurso.code}
          </p>
        </div>
      </div>
    );
  }

  const { data } = recurso;

  if (!data.ligado) {
    return (
      <div className="max-w-3xl space-y-4 text-sm">
        <h1 className="text-2xl font-bold">Motor</h1>
        <div className="mt-4 rounded-card border border-line bg-card p-4 shadow-card">
          <p className="font-medium">O motor está desligado.</p>
          <p className="mt-1 text-ink-muted">{data.motivo}</p>
          <p className="mt-2 text-xs text-ink-faint">
            Desligado não é falha: é o estado configurado. Enquanto estiver assim, nenhuma fila
            existe no banco e nenhuma etapa é executada.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 text-sm">
      <header>
        <h1 className="text-2xl font-bold">Motor</h1>
        <p className="mt-1 text-ink-muted">
          {data.estagios_ligados.length} de {data.filas.length} etapas com executor.{' '}
          {data.mortos > 0
            ? `${data.mortos} item(ns) esperando decisão humana.`
            : 'Nenhum item esperando decisão humana.'}
        </p>
      </header>

      {data.achados.length > 0 ? (
        <section className="mt-4 space-y-3">
          <h2 className="text-xs font-semibold uppercase tracking-wider text-ink-muted">
            O que pede ação
          </h2>
          {data.achados.map((a) => (
            <div
              key={`${a.fila}-${a.tipo}`}
              className={`rounded border p-4 ${TIPO_CLASSE[a.tipo] ?? 'border-line bg-surface'}`}
            >
              <p className="font-medium">
                {TIPO_LABEL[a.tipo] ?? a.tipo}: {a.fila}
              </p>
              {/* O QUE FAZER antes do dado tecnico. Alerta e para quem age. */}
              <p className="mt-1">{a.o_que_fazer}</p>
              <p className="mt-2 text-xs text-ink-faint">Quem resolve: {a.quem_resolve}</p>
            </div>
          ))}
        </section>
      ) : (
        <p className="mt-4 rounded-card border border-line bg-card p-4 shadow-card">
          Nada pede ação agora. Nenhuma fila parada, nenhum item na fila humana.
        </p>
      )}

      <section className="mt-6">
        <h2 className="text-xs font-semibold uppercase tracking-wider text-ink-muted">As etapas</h2>
        <div className="mt-2 overflow-x-auto">
          <table className="w-full min-w-[52rem] border-collapse text-left">
            <thead>
              <tr className="border-b border-line text-xs uppercase tracking-wide text-ink-muted">
                <th className="py-2 pr-4">Etapa</th>
                <th className="py-2 pr-4">Executor</th>
                <th className="py-2 pr-4 text-right">Esperando</th>
                <th className="py-2 pr-4 text-right">Rodando</th>
                <th className="py-2 pr-4 text-right">Fila humana</th>
                <th className="py-2">Quem alimenta</th>
              </tr>
            </thead>
            <tbody>
              {data.filas.map((f) => (
                <tr key={f.nome} className="border-b border-line/60 align-top">
                  <td className="py-2 pr-4 font-medium">{f.nome}</td>
                  <td className="py-2 pr-4">
                    {f.tem_trabalhador ? (
                      'existe'
                    ) : (
                      <span className="text-warn-ink">não construído</span>
                    )}
                  </td>
                  <td className="py-2 pr-4 text-right tabular-nums">{f.pendentes}</td>
                  <td className="py-2 pr-4 text-right tabular-nums">{f.ativos}</td>
                  <td className="py-2 pr-4 text-right tabular-nums">{f.mortos}</td>
                  <td className="py-2 text-ink-muted">{f.gatilho}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mt-2 text-xs text-ink-faint">
          Etapa sem executor não é etapa lenta: é trabalho que não vai acontecer até alguém
          construí-la. A coluna &quot;quem alimenta&quot; diz de onde vem o trabalho de cada fila.
        </p>
      </section>
    </div>
  );
}
